import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { endpoints } from '../endpoints';
import { AccessType, AppUser, BuildingContact, LogEntry } from '../models/user.model';
import { ResponseCode } from '../models/alert.model';
import { LockdownMessage } from '../models/message.model';
import { ErrorService } from './error.service';

export interface AlertLog {
  id:                 number;
  alertTypeId:        number;
  alertType:          string;
  alertBuildingId:    number;
  alertBuildingName:  string;
  userSubmitted:      number;
  userNameSubmitted:  string;
  dateSubmitted:      string;
  latitude:           string;
  longitude:          string;
  buildingLevelId:    number | null;
  buildingLevelName:  string | null;
  alertReceiverTypeId: number;
}

export interface UserStatusLog {
  id:                number;
  alertId:           number;
  userId:            number;
  userName:          string;
  userStatus:        ResponseCode;
  userStatusName:    string;
  dateTimeSubmitted: string;
}

export interface AlertUserStatus extends UserStatusLog {
  userEmail: string | null;
}

export interface AlertRecipient {
  id:                     number;
  recipientUserContactId: number;
  recipientUserType:      number;
  displayName:            string;
  orgWide:                boolean;
}

const ACCESS_TYPE_TO_PERMISSION: Record<AccessType, number> = {
  [AccessType.AdminAppUser]: 1,
  [AccessType.AppUser]:      2,
  [AccessType.MessageOnly]:  3,
};

const PERMISSION_TO_ACCESS_TYPE: Record<number, AccessType> = {
  1: AccessType.AdminAppUser,
  2: AccessType.AppUser,
  3: AccessType.MessageOnly,
};

interface RawLockdownUser {
  id:            number;
  userContactId: number;
  userType:      number;
  clientId:      number;
  buildingName:  string | null;
  permission:    number;
  inherited:     number | null;
  smsConsent:    number | null;
  userName:      string | null;
  userEmail:     string | null;
  userTitle:     string | null;
}

interface LockdownUsersResponse {
  has_error: boolean;
  message:   string;
  data:      RawLockdownUser[];
}

interface RawUser {
  userId:      number;
  displayName: string;
  name:        string;
  email:       string;
  title?:      string;
  phone?:      string;
}

interface RawContact {
  contactId:   number;
  displayName: string;
  firstName:   string;
  lastName:    string;
  email:       string;
  title?:      string;
  phone?:      string;
}

interface RawResponse {
  has_error: boolean;
  message:   string;
  data: {
    users:    RawUser[];
    contacts: RawContact[];
  };
}

export interface UsersAndContacts {
  users:    BuildingContact[];
  contacts: BuildingContact[];
}

function mapUser(raw: RawUser): BuildingContact {
  return {
    id:         `user_${raw.userId}`,
    name:       raw.name?.trim() || raw.displayName,
    email:      raw.email,
    department: raw.title ?? ''
  };
}

function mapContact(raw: RawContact): BuildingContact {
  return {
    id:         `contact_${raw.contactId}`,
    name:       `${raw.firstName} ${raw.lastName}`.trim() || raw.displayName,
    email:      raw.email,
    department: raw.title ?? ''
  };
}

interface RawLockdownMessage {
  id:          number;
  subject:     string | null;
  body:        string | null;
  sender:      string;
  created:     string | null;
  type:        number;
  sentTo:      string[];
  buildingId:  number;
  buildingName: string | null;
}

// Backend sends a single formatted string, e.g. "(388130) ABC XYZ (ac.xyz@test.io)"
// (see get_display_name in lockdown_v3_service.py). Split it into name/email and drop the id.
function parseSenderDisplay(raw: string): { name: string; email: string | null } {
  const withoutId = raw.replace(/^\(\d+\)\s*/, '').trim();

  const trailingParen = withoutId.match(/^(.*)\s\(([^()]+)\)$/);
  if (trailingParen && trailingParen[2].includes('@')) {
    return { name: trailingParen[1] || trailingParen[2], email: trailingParen[2] };
  }

  if (!withoutId.includes(' ') && withoutId.includes('@')) {
    return { name: withoutId, email: withoutId };
  }

  return { name: withoutId || raw, email: null };
}

function mapMessage(raw: RawLockdownMessage): LockdownMessage {
  const { name, email } = parseSenderDisplay(raw.sender);
  return {
    id:          raw.id,
    subject:     raw.subject,
    body:        raw.body,
    senderName:  name,
    senderEmail: email,
    created:     raw.created,
    type:        raw.type,
    sentTo:      raw.sentTo ?? [],
    clientId:    raw.buildingId,
    clientName:  raw.buildingName ?? undefined
  };
}

@Injectable({ providedIn: 'root' })
export class LockdownUserService {
  constructor(
    private http:         HttpClient,
    private errorService: ErrorService
  ) {}

  addUser(buildingId: string, contactId: string, accessType: AccessType, isContact: boolean): Observable<void> {
    const rawId     = contactId.split('_').pop()!;
    const permission = ACCESS_TYPE_TO_PERMISSION[accessType];
    const userType  = isContact ? 2 : 1;

    const url = environment.apiBaseUrl +
      endpoints.addLockdownUser
        .replace(':userContactId', rawId)
        .replace(':permission', String(permission));

    return this.http.post<any>(url, null, {
      params:          { user_type: String(userType), client_id: buildingId },
      withCredentials: environment.withCredentials
    }).pipe(
      map(() => void 0),
      catchError(err => this.errorService.handleError(err))
    );
  }

  updateUser(buildingId: string, contactId: string, isContact: boolean, accessType: AccessType): Observable<void> {
    const rawId    = contactId.split('_').pop()!;
    const userType = isContact ? 2 : 1;

    const url = environment.apiBaseUrl +
      endpoints.updateLockdownUser
        .replace(':userContactId', rawId)
        .replace(':clientId',     buildingId)
        .replace(':userType',     String(userType));

    return this.http.put<any>(url, null, {
      params:          { permission: String(ACCESS_TYPE_TO_PERMISSION[accessType]) },
      withCredentials: environment.withCredentials
    }).pipe(
      map(() => void 0),
      catchError(err => this.errorService.handleError(err))
    );
  }

  deleteUser(buildingId: string, contactId: string, isContact: boolean): Observable<void> {
    const rawId    = contactId.split('_').pop()!;
    const userType = isContact ? 2 : 1;

    const url = environment.apiBaseUrl +
      endpoints.deleteLockdownUser
        .replace(':userContactId', rawId)
        .replace(':clientId',     buildingId)
        .replace(':userType',     String(userType));

    return this.http.delete<any>(url, { withCredentials: environment.withCredentials }).pipe(
      map(() => void 0),
      catchError(err => this.errorService.handleError(err))
    );
  }

  getUsers(buildingId: string): Observable<AppUser[]> {
    const lockdownUrl = environment.apiBaseUrl +
      endpoints.getLockdownUsersForClient.replace(':clientId', buildingId);

    return this.http
      .get<LockdownUsersResponse>(lockdownUrl, { withCredentials: environment.withCredentials })
      .pipe(
        map(res => (res.data ?? []).map<AppUser>(rec => ({
          id:          rec.userType === 2 ? `contact_${rec.userContactId}` : `user_${rec.userContactId}`,
          name:        (rec.userName ?? '').trim() || `Unknown user (${rec.userContactId})`,
          email:       rec.userEmail ?? '',
          department:  rec.userTitle ?? '',
          accessType:  PERMISSION_TO_ACCESS_TYPE[rec.permission] ?? AccessType.AppUser,
          isContact:   rec.userType === 2,
          inherited:   rec.inherited === 1,
          clientId:    rec.clientId,
          clientName:  rec.buildingName ?? undefined,
          smsConsent:  rec.smsConsent,
        }))),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getLogs(buildingId: string): Observable<LogEntry[]> {
    const url = environment.apiBaseUrl +
      endpoints.getLogs.replace(':clientId', buildingId);

    return this.http
      .get<{ has_error: boolean; message: string; data: LogEntry[] }>(
        url, { withCredentials: environment.withCredentials }
      ).pipe(
        map(res => res.data ?? []),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getAlerts(buildingId: string): Observable<AlertLog[]> {
    const url = environment.apiBaseUrl +
      endpoints.getAlerts.replace(':clientId', buildingId);
    return this.http
      .get<{ has_error: boolean; message: string; data: AlertLog[] }>(
        url, { withCredentials: environment.withCredentials }
      ).pipe(
        map(res => res.data ?? []),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getUserStatuses(buildingId: string): Observable<UserStatusLog[]> {
    const url = environment.apiBaseUrl +
      endpoints.getUserStatuses.replace(':clientId', buildingId);
    return this.http
      .get<{ has_error: boolean; message: string; data: UserStatusLog[] }>(
        url, { withCredentials: environment.withCredentials }
      ).pipe(
        map(res => res.data ?? []),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getUserStatusesForAlert(buildingId: string, alertId: number): Observable<AlertUserStatus[]> {
    const url = environment.apiBaseUrl +
      endpoints.getUserStatusesForAlert
        .replace(':clientId', buildingId)
        .replace(':alertId', String(alertId));
    return this.http
      .get<{ has_error: boolean; message: string; data: AlertUserStatus[] }>(
        url, { withCredentials: environment.withCredentials }
      ).pipe(
        map(res => res.data ?? []),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getRecipientsForAlert(alertId: number): Observable<AlertRecipient[]> {
    const url = environment.apiBaseUrl +
      endpoints.getRecipientsForAlert.replace(':alertId', String(alertId));
    return this.http
      .get<{ has_error: boolean; message: string; data: AlertRecipient[] }>(
        url, { withCredentials: environment.withCredentials }
      ).pipe(
        map(res => res.data ?? []),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getMessages(buildingId: string, startingIndex = 0, pageSize = 50): Observable<LockdownMessage[]> {
    const url = environment.apiBaseUrl +
      endpoints.getLockdownMessages.replace(':buildingId', buildingId);

    return this.http
      .get<{ has_error: boolean; message: string; data: RawLockdownMessage[] }>(url, {
        params:          { pageSize: String(pageSize), startingIndex: String(startingIndex) },
        withCredentials: environment.withCredentials
      }).pipe(
        map(res => (res.data ?? []).map(mapMessage)),
        catchError(err => this.errorService.handleError(err))
      );
  }

  getUsersAndContacts(buildingId: string): Observable<UsersAndContacts> {
    const url = environment.apiBaseUrl +
      endpoints.getBuildingAllUsersAndContacts.replace(':buildingId', buildingId);

    return this.http
      .get<RawResponse>(url, { withCredentials: environment.withCredentials })
      .pipe(
        map(res => ({
          users:    (res.data?.users    ?? []).map(mapUser),
          contacts: (res.data?.contacts ?? []).map(mapContact)
        })),
        catchError(err => this.errorService.handleError(err))
      );
  }
}
